import { SphereTooltip } from "@components/common/sphere-tooltip";
import { ArrowDownIcon, blue } from "@flat-ui";
import {
  Box,
  Button,
  ButtonProps,
  Menu,
  PopoverOrigin,
  useTheme,
} from "@mui/material";
import { PropsWithChildren, useState } from "react";
import {
  ColorProp,
  getRippleColorWithOpacity,
} from "@utils/ripple-color-utils";
import { SphereButtonProps } from "@components/common/sphere-button";

interface Props
  extends Pick<ButtonProps, "startIcon">,
    Pick<SphereButtonProps, "isDisabled"> {
  /** The text to show for the button */
  buttonTitle?: JSX.Element | string;

  /** Text to show as tooltip */
  tooltipText?: string;

  /**
   * Optional sx properties to override the button style.
   * It accepts a variable to have separated style for when menu is open
   */
  buttonSx?: (isMenuOpen: boolean) => ButtonProps["sx"];

  /** If true, the menu item width (dropdown) will be equal to button width */
  isMenuWidthEqualToButton?: boolean;

  /** If true, the button will show an end icon */
  shouldShowEndIcon?: boolean;

  /**
   * Optional background color to apply to the ripple effect on hover and on pressed.
   * This must not contain the opacity value otherwise the ripple color will be static.
   * For ripple, opacity is applied internally with the value 15%.
   * If not provided, defaults to transparent (no background).
   */
  rippleColor?: ColorProp;

  /**
   * Defines the position of the dropdown menu relative to its trigger.
   *
   * Defaults to aligning the bottom-right of the button with
   * the top-right of the menu.
   */
  menuPosition?: {
    anchorOrigin?: PopoverOrigin;
    transformOrigin?: PopoverOrigin;
  };
}

/** Renders text button having a menu opened as a dropdown */
export function FaroTextMenuButton({
  buttonTitle,
  tooltipText,
  isDisabled,
  children,
  buttonSx,
  isMenuWidthEqualToButton = false,
  shouldShowEndIcon = true,
  rippleColor,
  startIcon,
  menuPosition,
}: PropsWithChildren<Props>): JSX.Element {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);

  // Calculate ripple color with opacity
  const rippleColorWithOpacity = getRippleColorWithOpacity(rippleColor, theme);

  return (
    <SphereTooltip title={tooltipText} shouldShowTooltip={!isMenuOpen}>
      <>
        <Button
          disabled={isDisabled}
          aria-controls={isMenuOpen ? "faro-text-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={!!isMenuOpen}
          variant="text"
          disableElevation
          disableRipple
          draggable={false}
          onClick={(event) => setAnchorEl(event.currentTarget)}
          endIcon={shouldShowEndIcon ? <ArrowDownIcon /> : undefined}
          startIcon={startIcon}
          sx={{
            fontSize: "12px",
            textTransform: "none",
            color: blue[500],
            borderRadius: rippleColorWithOpacity ?? "0",
            "&:hover": {
              backgroundColor: rippleColorWithOpacity ?? "transparent",
            },
            "&:focus-visible": {
              backgroundColor: rippleColorWithOpacity ?? "transparent",
            },
            ...(rippleColor && {
              "&& .MuiTouchRipple-child": {
                backgroundColor: `${rippleColor}3F`,
              },
            }),
            ...buttonSx?.(isMenuOpen),
          }}
        >
          {buttonTitle}
        </Button>
        <Menu
          id="faro-text-menu"
          anchorOrigin={
            menuPosition?.anchorOrigin ?? {
              vertical: "bottom",
              horizontal: "right",
            }
          }
          transformOrigin={
            menuPosition?.transformOrigin ?? {
              vertical: "top",
              horizontal: "right",
            }
          }
          anchorEl={anchorEl}
          open={isMenuOpen}
          onClose={() => setAnchorEl(null)}
          slotProps={{
            list: {
              sx: {
                width: isMenuWidthEqualToButton
                  ? anchorEl && anchorEl.offsetWidth
                  : "225px",
              },
            },
          }}
        >
          <Box
            component="div"
            onClick={() => setAnchorEl(null)}
            sx={{
              padding: "0px 8px",
              "& .MuiList-root": {
                // Fix bottom padding, since each menu item has a bottom margin of 2px
                paddingBottom: "6px",
              },
            }}
          >
            {children}
          </Box>
        </Menu>
      </>
    </SphereTooltip>
  );
}
